import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { RepairOrderStatusE, WipStatusE } from 'declarations/enums/wip';
import { WipSortFieldE, WipSortOrderE } from 'declarations/enums/wip';

export interface WipSnackbarStateI {
  message: string;
  subMessage?: string;
  severity: 'success' | 'error' | 'warning';
}

export interface WipPagingStateI {
  page: number;
  pageSize: number;
}

export interface WipSortingStateI {
  parameter: WipSortFieldE;
  order: WipSortOrderE;
}

export interface WipAppliedFiltersI {
  roofTopIds: string[];
  oemFamilyIds: string[];
  wipStatuses: WipStatusE[];
  claimStatuses: string[];
  repairOrderStatuses: RepairOrderStatusE[];
  backlog: boolean | null;
  createdAtFrom?: string;
  createdAtTo?: string;
}

/**
 * Picker-level configuration for a single WIP table column. The `order` of `columnOrder` (below)
 * is the visual order; `visible` toggles the column on/off; `required` columns cannot be hidden
 * (mirrors WarrQueue's `REQUIRED_COLUMN_LABELS` enforcement in the Customize Columns modal).
 */
export interface WipColumnConfigI {
  id: string;
  visible: boolean;
  required: boolean;
}

export interface WipStateI {
  paging: WipPagingStateI;
  sorting: WipSortingStateI;
  selectedRoofTopId: string | null;
  searchTerm: string;
  appliedFilters: WipAppliedFiltersI;
  /**
   * Ordered list of all WIP columns with visibility flags. Empty means the table should fall
   * back to the default configuration order + defaultVisible flags. The Customize Columns modal
   * is the only writer; the table component reads + filters out hidden columns.
   */
  columnOrder: WipColumnConfigI[];
  /**
   * IDs of WIP records currently selected via the table's checkbox column. Mirrors the
   * WarrQueue WIP selection model used to drive batch row actions.
   */
  selectedWipIds: string[];
  snackbar: WipSnackbarStateI;
}

export const EMPTY_WIP_FILTERS: WipAppliedFiltersI = {
  roofTopIds: [],
  oemFamilyIds: [],
  wipStatuses: [],
  claimStatuses: [],
  repairOrderStatuses: [],
  backlog: null,
  createdAtFrom: undefined,
  createdAtTo: undefined,
};

const initialState: WipStateI = {
  paging: {
    page: 1,
    pageSize: 50,
  },
  sorting: {
    parameter: WipSortFieldE.RepairOrderNumber,
    order: WipSortOrderE.Asc,
  },
  selectedRoofTopId: null,
  searchTerm: '',
  appliedFilters: EMPTY_WIP_FILTERS,
  columnOrder: [],
  selectedWipIds: [],
  snackbar: {
    message: '',
    severity: 'success',
  },
};

const wipSlice = createSlice({
  name: 'wip',
  initialState,
  reducers: {
    setWipPage: (state, action: PayloadAction<number>) => {
      state.paging.page = action.payload;
    },
    setWipPageSize: (state, action: PayloadAction<number>) => {
      state.paging.pageSize = action.payload;
      state.paging.page = 1;
    },
    resetWipPageNumber: state => {
      state.paging.page = 1;
    },
    setWipSorting: (state, action: PayloadAction<WipSortingStateI>) => {
      state.sorting = action.payload;
      state.paging.page = 1;
    },
    setSelectedWipRoofTopId: (state, action: PayloadAction<string | null>) => {
      state.selectedRoofTopId = action.payload;
      state.paging.page = 1;
    },
    setWipSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.paging.page = 1;
    },
    setWipAppliedFilters: (state, action: PayloadAction<WipAppliedFiltersI>) => {
      state.appliedFilters = action.payload;
      state.paging.page = 1;
    },
    clearWipAppliedFilters: state => {
      state.appliedFilters = EMPTY_WIP_FILTERS;
      state.paging.page = 1;
    },
    setWipColumnOrder: (state, action: PayloadAction<WipColumnConfigI[]>) => {
      state.columnOrder = action.payload;
    },
    toggleWipColumnVisibility: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.columnOrder = state.columnOrder.map(
        (c): WipColumnConfigI => (c.id === id && !c.required ? { ...c, visible: !c.visible } : c)
      );
    },
    reorderWipColumn: (
      state,
      action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>
    ) => {
      const { sourceIndex, destinationIndex } = action.payload;
      if (
        sourceIndex === destinationIndex ||
        sourceIndex < 0 ||
        destinationIndex < 0 ||
        sourceIndex >= state.columnOrder.length ||
        destinationIndex >= state.columnOrder.length
      ) {
        return;
      }
      const next = state.columnOrder.slice();
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(destinationIndex, 0, moved);
      state.columnOrder = next;
    },
    resetWipColumnOrder: state => {
      state.columnOrder = [];
    },
    setWipSelectedIds: (state, action: PayloadAction<string[]>) => {
      state.selectedWipIds = action.payload;
    },
    toggleWipSelectedId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!id) return;
      if (state.selectedWipIds.includes(id)) {
        state.selectedWipIds = state.selectedWipIds.filter((existing): boolean => existing !== id);
      } else {
        state.selectedWipIds = [...state.selectedWipIds, id];
      }
    },
    clearWipSelectedIds: state => {
      state.selectedWipIds = [];
    },
    updateWipSnackbar: (state, action: PayloadAction<WipSnackbarStateI>) => {
      state.snackbar = action.payload;
    },
    clearWipSnackbar: state => {
      state.snackbar = { message: '', severity: 'success' };
    },
  },
});

export const {
  setWipPage,
  setWipPageSize,
  resetWipPageNumber,
  setWipSorting,
  setSelectedWipRoofTopId,
  setWipSearchTerm,
  setWipAppliedFilters,
  clearWipAppliedFilters,
  setWipColumnOrder,
  toggleWipColumnVisibility,
  reorderWipColumn,
  resetWipColumnOrder,
  setWipSelectedIds,
  toggleWipSelectedId,
  clearWipSelectedIds,
  updateWipSnackbar,
  clearWipSnackbar,
} = wipSlice.actions;

export const selectWipPage = (state: { wip: WipStateI }): WipPagingStateI => state.wip.paging;
export const selectWipSorting = (state: { wip: WipStateI }): WipSortingStateI => state.wip.sorting;
export const selectWipSelectedRoofTopId = (state: { wip: WipStateI }): string | null =>
  state.wip.selectedRoofTopId;
export const selectWipSearchTerm = (state: { wip: WipStateI }): string => state.wip.searchTerm;
export const selectWipAppliedFilters = (state: { wip: WipStateI }): WipAppliedFiltersI =>
  state.wip.appliedFilters;
export const selectWipColumnOrder = (state: { wip: WipStateI }): WipColumnConfigI[] =>
  state.wip.columnOrder;
export const selectWipSelectedIds = (state: { wip: WipStateI }): string[] =>
  state.wip.selectedWipIds;
export const selectWipSnackbar = (state: { wip: WipStateI }): WipSnackbarStateI =>
  state.wip.snackbar;

export default wipSlice.reducer;
